import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";
import { pillarArticles, type ArticleLocale } from "@/mocks/articles";

function getLocale(lang: string, locales: { en: ArticleLocale; fr: ArticleLocale; da: ArticleLocale; de: ArticleLocale }): ArticleLocale {
  const key = lang.split("-")[0] as "en" | "fr" | "da" | "de";
  return locales[key] || locales.en;
}

export default function ArticleDetailPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = pillarArticles.find((a) => a.slug === slug);
  const locale = article ? getLocale(i18n.language, article.locales) : null;

  if (!article || !locale) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-20 px-4 md:px-6 text-center">
          <h1 className="font-heading font-bold text-2xl text-foreground-950">{t("articleNotFound")}</h1>
          <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-primary-600 hover:text-primary-700">
            <i className="ri-arrow-left-line"></i>
            {t("backHome")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-24 pb-14 md:pt-28 md:pb-20 px-4 md:px-6">
        <article className="max-w-3xl mx-auto">
          {/* Breadcrumb / back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            {t("backHome")}
          </Link>

          {/* Cover image */}
          <div className="mt-6 rounded-lg overflow-hidden border border-background-200 bg-background-100">
            <img src={article.image} alt={locale.title} className="w-full h-52 md:h-72 object-cover object-top" />
          </div>

          {/* Header */}
          <header className="mt-6">
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 leading-tight">
              {locale.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {t("author")}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {t("updated")}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} {t("readTime")}
              </span>
            </div>
          </header>

          {/* Intro */}
          <p className="text-base md:text-lg text-foreground-700 leading-relaxed mt-6">{locale.intro}</p>

          {/* TOC */}
          <nav className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading font-semibold text-sm text-foreground-900 mb-3">{t("tocTitle")}</h2>
            <ul className="flex flex-col gap-2">
              {locale.sections.map((s, idx) => (
                <li key={idx}>
                  <a href={`#${article.slug}-${idx}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Sections */}
          <div className="mt-10 flex flex-col gap-10">
            {locale.sections.map((s, idx) => (
              <section key={idx} id={`${article.slug}-${idx}`} className="scroll-mt-24">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">{s.heading}</h2>
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>

          {/* CTA + internal links */}
          <div className="mt-12 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">{t("ctaTitle")}</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-3">{t("ctaDesc")}</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-6">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t("ctaLabel")}
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                {t("buyLabel")}
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                {t("backLabel")}
              </Link>
            </div>
          </div>

          {/* Internal link — more guides */}
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm text-foreground-700">{t("moreGuides")}</p>
            <Link
              to="/#articles"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-grid-line"></i>
              {t("browseGuides")}
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}